import { useDispatch, useSelector } from 'react-redux';
import {
  setListing,
  setSelected,
  setNextToken,
} from '../store/ducks/ticketsScan';
import { HeadCell, ListingVariables } from '../models/app';
import useApp from './useApp';
import { generateClient } from '@aws-amplify/api';
import { ModelEventFilterInput, ModelSortDirection } from '../models/GQL_API';
import { TicketsScan } from '../models';
import {
  TicketScansByEventId,
  TicketsScanByScannedById,
  listEvents,
  listTicketsScans,
} from '../graphql/queries';
import { toDayRangeIso } from '../helpers/dateRange';

const client = generateClient();
const useHostScans = (listingName: string, singleName: string) => {
  const dispatch = useDispatch();
  const { showConfirm, showError } = useApp();

  const nextToken = useSelector((state: any) => state.ticketsScan['nextToken']);
  const ticketsScanListing = useSelector(
    (state: any) => state.ticketsScan['listing'],
  );
  const generalFilters = useSelector((state: any) => state.generalFilters);
  const selectedAdmin = useSelector((state: any) => state.admins.selected);

  async function fetch(params: ListingVariables) {
    try {
      if (!selectedAdmin) {
        return ticketsScanListing;
      }
      const { searchText, generalFilters } = params;
      // Only needed as a fallback when no (global) event is selected.
      const upcomingEvent = generalFilters?.event
        ? undefined
        : await fetchUpComingEvent();

      const filter: any = {};
      filter.or = [];
      if (searchText.length > 0) {
        filter.or.push({ guestName: { contains: searchText.toLowerCase() } });
      }
      // scannedAt is the sort key of both indexes: use it as key condition.
      let scannedAtCondition: any;
      const scannedAtRange = toDayRangeIso(
        generalFilters?.fromDate,
        generalFilters?.toDate,
      );
      if (scannedAtRange) {
        scannedAtCondition = { between: scannedAtRange };
      }
      const eventId: string | undefined =
        generalFilters?.event && generalFilters.event !== ''
          ? generalFilters.event
          : upcomingEvent?.id;
      if (filter.or && filter.or.length === 0) {
        delete filter.or;
      }
      filter.deleted = { eq: '0' };

      let currentNextToken: string | null | undefined;
      let responseListing: any[] = [];
      if (selectedAdmin.sub) {
        // Scans of the selected admin (TicketsScanByScannedById GSI),
        // narrowed to the event.
        if (eventId) filter.eventId = { eq: eventId };
        const variables: any = {
          scannedById: selectedAdmin.sub,
          filter,
          limit: 1000,
          nextToken,
          sortDirection: ModelSortDirection.DESC,
        };
        if (scannedAtCondition) variables.scannedAt = scannedAtCondition;
        const result: any = await client.graphql({
          query: TicketsScanByScannedById,
          variables,
          authMode: 'userPool',
        });
        currentNextToken = result.data.TicketsScanByScannedById.nextToken;
        responseListing = result.data.TicketsScanByScannedById.items;
      } else if (eventId) {
        // No admin id: all scans of the event (TicketScansByEventId GSI).
        const variables: any = {
          eventId,
          filter,
          limit: 1000,
          nextToken,
          sortDirection: ModelSortDirection.DESC,
        };
        if (scannedAtCondition) variables.scannedAt = scannedAtCondition;
        const result: any = await client.graphql({
          query: TicketScansByEventId,
          variables,
          authMode: 'userPool',
        });
        currentNextToken = result.data.TicketScansByEventId.nextToken;
        responseListing = result.data.TicketScansByEventId.items;
      } else {
        // Neither an admin id nor an event: nothing to key on (rare).
        if (scannedAtCondition) filter.scannedAt = scannedAtCondition;
        const groupsList: any = await client.graphql<TicketsScan>({
          query: listTicketsScans,
          variables: { filter, limit: 1000, nextToken: nextToken },
          authMode: 'userPool',
        });
        currentNextToken = groupsList.data.listTicketsScans.nextToken;
        responseListing = groupsList.data.listTicketsScans.items;
      }
      // Filters changed while this page was loading: drop the old response.
      if (params.isStale?.()) return [];

      let listing = [...ticketsScanListing, ...responseListing];
      dispatch(setListing(listing));
      dispatch(setNextToken(currentNextToken));
      return listing;
    } catch (err: Error | any) {
      showError(err.message || err);
      return [];
    }
  }

  async function fetchUpComingEvent() {
    try {
      const limit = 10000;
      const filter: ModelEventFilterInput = {
        deleted: { eq: '0' },
        published: {
          eq: true,
        },
        endDate: {
          gt: new Date().toISOString(),
        },
      };

      const eventList = await client.graphql({
        query: listEvents,
        variables: { filter, limit: 100000 },
        authMode: 'userPool',
      });
      const listing = eventList.data.listEvents.items;
      return listing[0];
    } catch (err: Error | any) {
      showError(err.message);
    }
  }

  const headCells: readonly HeadCell[] = [
    {
      id: 'guestName',
      numeric: false,
      disablePadding: false,
      label: 'Name / Email / Phone',
    },
    {
      id: 'scannedByName',
      numeric: false,
      disablePadding: false,
      label: 'Scanned By',
    },
    {
      id: 'ticketType',
      numeric: false,
      disablePadding: false,
      label: 'Ticket Type',
    },
    {
      id: 'status',
      numeric: false,
      disablePadding: false,
      label: 'Status',
    },
    {
      id: 'gateName',
      numeric: false,
      disablePadding: false,
      label: 'Gate',
    },
    {
      id: 'scannedAt',
      numeric: false,
      disablePadding: false,
      label: 'Scanned At',
    },

    // {
    //   id: 'actions',
    //   numeric: true,
    //   disablePadding: false,
    //   label: '',
    // },
  ];
  const dataCells: readonly string[] = [
    'guestName',
    'scannedByName',
    'ticketType',
    'status',
    'gateName',
    'scannedAt',
  ];

  const api: any = {};
  api[`${listingName}HeadCells`] = headCells;
  api[`${listingName}DataCells`] = dataCells;
  api[`${listingName}Fetch`] = fetch;

  api[`${listingName}ChangeListing`] = (listing: TicketsScan[]) =>
    dispatch(setListing(listing));
  api[`${listingName}ChangeSelected`] = (conceptID: string) =>
    dispatch(setSelected(conceptID));
  api[`${listingName}ClearListing`] = () => dispatch(setListing([]));
  api[`${listingName}ClearNextToken`] = () => dispatch(setNextToken(null));
  api[`${listingName}NextToken`] = nextToken;
  api[`${listingName}Listing`] = ticketsScanListing;
  return api;
};
export default useHostScans;
